import React from 'react';
import Icon from '../../../components/AppIcon';

const RARITY_RING = {
  common:    { ring: 'ring-gray-400/50',   shadow: '',                                    shimmer: false },
  rare:      { ring: 'ring-blue-400/70',   shadow: 'shadow-blue-500/30',                  shimmer: false },
  epic:      { ring: 'ring-purple-400/70', shadow: 'shadow-purple-500/30',                shimmer: false },
  legendary: { ring: 'ring-yellow-400/80', shadow: 'shadow-yellow-400/50 shadow-lg',      shimmer: true  },
};

const BadgeCollection = ({ badges }) => {
  const getBadgeIcon = (type) => {
    const icons = {
      reading: 'BookOpen',
      quiz: 'Brain',
      pronunciation: 'Mic',
      completion: 'Trophy',
      streak: 'Flame',
      xp: 'Zap',
      avatar: 'Wand2',
      play: 'Theater',
      accuracy: 'Target',
      milestone: 'Star',
    };
    return icons?.[type] || 'Award';
  };

  const getBadgeColor = (type) => {
    const colors = {
      xp: 'from-yellow-500 to-amber-600',
      streak: 'from-orange-500 to-red-600',
      play: 'from-blue-500 to-indigo-600',
      avatar: 'from-purple-500 to-pink-600',
      quiz: 'from-green-500 to-teal-600',
      pronunciation: 'from-cyan-500 to-blue-600',
      accuracy: 'from-emerald-500 to-green-600',
      milestone: 'from-rose-500 to-pink-600',
      reading: 'from-violet-500 to-purple-600',
      completion: 'from-amber-500 to-yellow-600',
    };
    return colors?.[type] || 'from-muted to-muted-foreground';
  };

  // Normalise badge data — DB uses badge_name/badge_type/badge_description,
  // but some paths may already map to name/type/description.
  const normaliseBadge = (badge) => ({
    id: badge?.id,
    name: badge?.name || badge?.badge_name || 'Badge',
    description: badge?.description || badge?.badge_description || '',
    type: badge?.type || badge?.badge_type || 'milestone',
    rarity: badge?.rarity || 'common',
    earnedDate: badge?.earnedDate
      ? badge?.earnedDate
      : badge?.earned_at
      ? new Date(badge?.earned_at)?.toLocaleDateString()
      : '',
  });

  return (
    <div className="bg-card rounded-xl shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-semibold text-card-foreground flex items-center gap-2">
          <Icon name="Award" size={24} color="var(--color-secondary)" />
          Badge Collection
        </h2>
        <span className="text-sm text-muted-foreground">{badges?.length} earned</span>
      </div>
      {badges?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Award" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Complete activities to earn badges!</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {badges?.map((rawBadge) => {
            const badge = normaliseBadge(rawBadge);
            const rarityRing = RARITY_RING?.[badge?.rarity] || RARITY_RING?.common;
            return (
              <div
                key={badge?.id}
                className="relative group cursor-pointer"
              >
                <div className={`bg-gradient-to-br ${getBadgeColor(badge?.type)} rounded-lg p-4 text-center transition-transform duration-300 hover:scale-105 ring-2 ${rarityRing?.ring} ${rarityRing?.shadow} ${rarityRing?.shimmer ? 'legendary-badge-shimmer' : ''}`}>
                  <div className="w-16 h-16 mx-auto mb-2 bg-card-foreground/10 rounded-full flex items-center justify-center">
                    <Icon name={getBadgeIcon(badge?.type)} size={32} color="white" />
                  </div>
                  <h4 className="text-sm font-semibold text-white mb-1">{badge?.name}</h4>
                  <p className="text-xs text-white/80">{badge?.earnedDate}</p>
                  {/* Rarity label */}
                  <div className="mt-1.5">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      badge?.rarity === 'legendary' ? 'bg-yellow-400/20 text-yellow-200' :
                      badge?.rarity === 'epic'      ? 'bg-purple-400/20 text-purple-200' :
                      badge?.rarity === 'rare'? 'bg-blue-400/20 text-blue-200' : 'bg-white/10 text-white/60'
                    }`}>
                      {badge?.rarity === 'legendary' ? '✦ Legendary' :
                       badge?.rarity === 'epic'      ? '◆ Epic' :
                       badge?.rarity === 'rare'      ? '● Rare' : 'Common'}
                    </span>
                  </div>
                </div>
                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-popover border border-border rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                  <p className="text-xs text-popover-foreground font-medium">{badge?.description}</p>
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 -mt-1">
                    <div className="border-4 border-transparent border-t-popover"></div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BadgeCollection;